import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  ListItemIcon,
  Autocomplete,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  DriveFileMove as DriveFileMoveIcon,
  Check as CheckIcon,
  Undo as ReturnIcon
} from '@mui/icons-material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  FolderShared as FolderSharedIcon,
  CalendarMonth as CalendarMonthIcon,
  EventNote as EventNoteIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  CreateNewFolder as CreateNewFolderIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import { FiberManualRecord as StatusDotIcon } from '@mui/icons-material';
import { LessonPlanList } from '../components/LessonPlanList';
import { LessonPlanUpload } from '../components/LessonPlanUpload';
import { LessonPlanEdit } from '../components/LessonPlanEdit';
import { LessonPlanDetail } from '../components/LessonPlanDetail';
import { LessonPlanErrorBoundary } from '../components/LessonPlanErrorBoundary';
import { FolderSelector } from '../components/FolderSelector';
import { lessonPlanService } from '../services/lessonPlanService';
import { lessonPlanFolderService } from '../services/lessonPlanFolderService';
import { subjectService } from '../services/subjectService';
import { useAppStore } from '../stores/useAppStore';
import { isTeacher, canApproveLessonPlans } from '../utils/rbac';
import type { LessonPlan, LessonPlanFormData, LessonPlanFolder } from '../types';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from '../components/ToastNotification';
import { ExportDialog } from '../components/ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { isStudent } from '../utils/rbac';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { vi } from 'date-fns/locale';
import { Settings as SettingsIcon, Error as ErrorIcon } from '@mui/icons-material';
import { logger } from '../utils/logger';
import { templateService, LessonPlanTemplate } from '../services/templateService';
import { TemplateSelectionDialog } from '../components/TemplateSelectionDialog';
import { CloudDownload as CloneIcon } from '@mui/icons-material';


// Constants
const LESSON_PLAN_STATUSES = [
  { value: '', label: 'Tất cả' },
  { value: 'PendingSubmit', label: 'Chờ nộp' },
  { value: 'Draft', label: 'Bản nháp' },
  { value: 'Pending', label: 'Chờ duyệt' },
  { value: 'UnderReview', label: 'Đang xem xét' },
  { value: 'Approved', label: 'Đã duyệt' },
  { value: 'NeedsRevision', label: 'Cần chỉnh sửa' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lesson-plan-tabpanel-${index}`}
      aria-labelledby={`lesson-plan-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LessonPlanManagementPage: React.FC = () => {
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const isTeacherRole = isTeacher(userRoles);
  // Super-admin luôn được xem "QL giáo án" (không phụ thuộc roles trong tenant)
  const canApprove = user?.isSuperAdmin === true ? true : canApproveLessonPlans(userRoles);
  // User có thể vừa là Teacher vừa là Admin/BGH. Chỉ coi là "teacher-only" khi KHÔNG có quyền duyệt.
  const isTeacherOnly = isTeacherRole && !canApprove;
  const canExport = !isStudent(userRoles);
  const [now, setNow] = useState<Date>(() => new Date());

  const getStatusLegendMeta = (status: string): { label: string; color: string } => {
    switch (status) {
      case 'Pending':
        return { label: 'Chờ duyệt', color: '#1976d2' };
      case 'PendingSubmit':
        return { label: 'Chờ nộp', color: '#6a1b9a' };
      case 'UnderReview':
        return { label: 'Đang xem xét', color: '#f9a825' };
      case 'Approved':
        return { label: 'Đã duyệt', color: '#2e7d32' };
      case 'NeedsRevision':
        return { label: 'Cần chỉnh sửa', color: '#fb8c00' };
      case 'Draft':
        return { label: 'Bản nháp', color: '#9e9e9e' };
      default:
        return { label: status, color: '#9e9e9e' };
    }
  };

  const StatusLegendRow = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
      {(['PendingSubmit', 'Pending', 'UnderReview', 'Approved', 'NeedsRevision'] as const).map((s) => {
        const meta = getStatusLegendMeta(s);
        return (
          <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StatusDotIcon sx={{ fontSize: 12, color: meta.color }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {meta.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  // State management
  // If Teacher, start with "Giáo Án Của Tôi" tab (index 0)
  const [activeTab, setActiveTab] = useState(isTeacherOnly ? 0 : 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'submissiondate'>('title'); // Mặc định sắp xếp theo tên

  // Data state
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [myLessonPlans, setMyLessonPlans] = useState<LessonPlan[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [myTotalCount, setMyTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [myPage, setMyPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [myTotalPages, setMyTotalPages] = useState(0);

  // Dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLessonPlan, setSelectedLessonPlan] = useState<LessonPlan | null>(null);
  const [lessonPlanToEdit, setLessonPlanToEdit] = useState<LessonPlan | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Bulk delete state for QL GIÁO ÁN tab
  const [selectedLessonPlanIds, setSelectedLessonPlanIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');
  const [bulkRevisionNotes, setBulkRevisionNotes] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [selectedMoveFolderId, setSelectedMoveFolderId] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  const { snackbar, showSuccess, showError, showInfo, hideSnackbar } = useSnackbar();

  // Virtual folder filter (client-side only) - Default to 'all'
  const [selectedFolderKey, setSelectedFolderKey] = useState<string>('all');

  // Combined filters state (for multi-criteria filtering)
  const [combinedFilters, setCombinedFilters] = useState<{
    year: number | null;
    subjectId: string | null;
    week: number | null;
    teacherId: string | null;
    status: string | null;
  }>({
    year: null,
    subjectId: null,
    week: null,
    teacherId: null,
    status: null
  });

  // Filter options from API
  const [filterTeachers, setFilterTeachers] = useState<Array<{ id: string; name: string }>>([]);
  const [filterSubjects, setFilterSubjects] = useState<string[]>([]);
  const [filterYears, setFilterYears] = useState<number[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Subjects from database for "Theo bộ môn" filter in "GIÁO ÁN CỦA TÔI" tab
  const [dbSubjects, setDbSubjects] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingDbSubjects, setLoadingDbSubjects] = useState(false);

  // Weeks for "Theo tuần" filter in "GIÁO ÁN CỦA TÔI" tab
  const [filterWeeks, setFilterWeeks] = useState<number[]>([]);

  // Weeks for "Theo tuần" filter in "QL GIÁO ÁN" tab (all lesson plans)
  const [allFilterWeeks, setAllFilterWeeks] = useState<number[]>([]);

  // Real folders from API
  const [folders, setFolders] = useState<LessonPlanFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>(null);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteFolderMessage, setDeleteFolderMessage] = useState('');

  // Edit folder dialog (rename + change parent)
  const [editFolderDialogOpen, setEditFolderDialogOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState<{ id: string; name: string; parentFolderId: string | null } | null>(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderParentId, setEditFolderParentId] = useState<string | null>(null);
  const [editFolderSaving, setEditFolderSaving] = useState(false);

  // Detail Action State (Approve/Review/Reject from Detail View)
  const [detailActionDialogOpen, setDetailActionDialogOpen] = useState(false);
  const [detailActionType, setDetailActionType] = useState<'approve' | 'review' | 'reject'>('approve');
  const [detailActionNotes, setDetailActionNotes] = useState('');
  const [detailActionProcessing, setDetailActionProcessing] = useState(false);

  // Export dialog states
  const [exportAllDialogOpen, setExportAllDialogOpen] = useState(false);
  const [exportMyDialogOpen, setExportMyDialogOpen] = useState(false);
  const [exportManageDialogOpen, setExportManageDialogOpen] = useState(false);
  const [quickUploadFolderId, setQuickUploadFolderId] = useState<string | null>(null);

  // Date range filter
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  // Submission period settings (Admin/BGH only)
  const [submissionPeriodDialogOpen, setSubmissionPeriodDialogOpen] = useState(false);
  const [submissionPeriodStart, setSubmissionPeriodStart] = useState<Date | null>(null);
  const [submissionPeriodEnd, setSubmissionPeriodEnd] = useState<Date | null>(null);
  const [submissionPeriodLoading, setSubmissionPeriodLoading] = useState(false);

  // Template state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [initialUploadFiles, setInitialUploadFiles] = useState<File[]>([]);

  const handleTemplateSelect = async (template: LessonPlanTemplate) => {
    setTemplateDialogOpen(false);
    try {
      setLoading(true);
      let file: File | null = null;
      if (template.file_url) {
        const response = await fetch(template.file_url);
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const filename = template.file_url.split('/').pop() || `${template.title}.docx`;
        file = new File([blob], filename, { type: blob.type });
      } else if (template.content) {
        const blob = new Blob([template.content], { type: 'text/html' });
        file = new File([blob], `${template.title}.html`, { type: 'text/html' });
      }

      if (file) {
        setInitialUploadFiles([file]);
        setUploadDialogOpen(true);
      } else {
        showError('Mẫu này không có nội dung.');
      }
    } catch (error) {
      console.error(error);
      showError('Không thể tải mẫu.');
    } finally {
      setLoading(false);
    }
  };

  // Load lesson plans
  const loadLessonPlans = useCallback(async (pageNum: number = 1, isMyPlans: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const filter = getFilterFromFolderKey();

      // Only apply teacher filter if not in "myPlans" mode (myPlans already filters by current user)
      const teacherId = isMyPlans ? null : filter.teacherId;

      // Logic filter Draft:
      // - Tab "GIÁO ÁN CỦA TÔI" (isMyPlans = true): 
      //   + Nếu có statusFilter: Áp dụng filter (ví dụ: "Chờ duyệt" chỉ hiển thị "Chờ duyệt", không hiển thị Draft)
      //   + Nếu không có statusFilter: Hiển thị TẤT CẢ giáo án của giáo viên, kể cả Draft
      // - Tab "QL GIÁO ÁN" (canApprove = true && isMyPlans = false): Chỉ hiển thị giáo án đã nộp (không Draft) của TẤT CẢ giáo viên
      const excludeDraft = canApprove && !isMyPlans;

      // Khi isMyPlans = true và có statusFilter, áp dụng filter
      // Khi isMyPlans = true và không có statusFilter, hiển thị tất cả (kể cả Draft)
      // Khi isMyPlans = false, áp dụng statusFilter như bình thường
      // Chỉ gửi statusFilter nếu nó không rỗng
      const finalStatusFilter = (statusFilter && statusFilter.trim() !== '') ? statusFilter : undefined;

      const params = {
        page: pageNum,
        pageSize: 10,
        searchTerm: filter.subjectName ? filter.subjectName : debouncedSearchTerm, // Use subject name filter if selected folder is subject name
        status: finalStatusFilter,
        excludeStatus: excludeDraft ? 'Draft' : undefined,
        myPlans: isMyPlans,
        submittedByUserId: teacherId || undefined,
        folderId: filter.folderId || undefined,
        subjectId: filter.subjectId || undefined,
        week: filter.week || undefined,
        dateFrom: filter.dateFrom ? filter.dateFrom.toISOString().split('T')[0] : (dateFrom ? dateFrom.toISOString().split('T')[0] : undefined),
        dateTo: filter.dateTo ? filter.dateTo.toISOString().split('T')[0] : (dateTo ? dateTo.toISOString().split('T')[0] : undefined),
        sortBy: sortBy
      };

      logger.log(`📋 Loading lesson plans - isMyPlans: ${isMyPlans}, excludeDraft: ${excludeDraft}, params:`, params);
      // Add timestamp to prevent caching
      const response = await lessonPlanService.getLessonPlans(params);
      logger.log(`📋 Response received - isMyPlans: ${isMyPlans}, lessonPlans count:`, response.lessonPlans?.length, 'totalCount:', response.totalCount, 'excludeDraft was:', excludeDraft);
      // Log status of each lesson plan to verify Draft filtering
      if (response.lessonPlans && response.lessonPlans.length > 0) {
        response.lessonPlans.forEach((lp, index) => {
          logger.log(`📋 Lesson plan ${index + 1} - status: ${lp.status}, title: ${lp.title}`);
        });
      }
      if (response.lessonPlans && response.lessonPlans.length > 0) {
        logger.log(`📋 First lesson plan - id:`, response.lessonPlans[0].id, 'title:', response.lessonPlans[0].title);
      }
      // Log first lesson plan to verify data is fresh
      if (response.lessonPlans && response.lessonPlans.length > 0) {
        logger.log('📋 First lesson plan in response - title:', response.lessonPlans[0].title, 'fileName:', response.lessonPlans[0].fileName, 'id:', response.lessonPlans[0].id);
        // Log all lesson plans titles for debugging
        response.lessonPlans.forEach((lp, index) => {
          logger.log(`📋 Lesson plan ${index + 1} - title:`, lp.title, 'id:', lp.id);
        });
      }

      if (isMyPlans) {
        setMyLessonPlans(Array.isArray(response.lessonPlans) ? response.lessonPlans : []);
        setMyTotalCount(response.totalCount);
        setMyTotalPages(Math.max(1, Math.ceil(response.totalCount / response.pageSize)));
        setMyPage(pageNum);
        logger.log(`📋 My lesson plans updated:`, Array.isArray(response.lessonPlans) ? response.lessonPlans.length : 0, 'myLessonPlans state:', myLessonPlans.length);
      } else {
        setLessonPlans(Array.isArray(response.lessonPlans) ? response.lessonPlans : []);
        setTotalCount(response.totalCount);
        setTotalPages(Math.max(1, Math.ceil(response.totalCount / response.pageSize)));
        setPage(pageNum);
        // logger.log(`📋 All lesson plans updated:`, Array.isArray(response.lessonPlans) ? response.lessonPlans.length : 0);
      }
    } catch (err) {
      logger.error('Error loading lesson plans:', err);
      setError('Không thể tải danh sách giáo án. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, statusFilter, dateFrom, dateTo, sortBy, canApprove, selectedFolderKey, combinedFilters]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Keep a lightweight ticking clock for submission period banner
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  // Load submission period for banner (safe to ignore if endpoint is restricted)
  useEffect(() => {
    let mounted = true;
    lessonPlanService
      .getSubmissionPeriod()
      .then((period) => {
        if (!mounted) return;
        if (period?.startDate && period?.endDate) {
          setSubmissionPeriodStart(new Date(period.startDate));
          setSubmissionPeriodEnd(new Date(period.endDate));
        }
      })
      .catch(() => {
        // Ignore - banner is optional
      });
    return () => {
      mounted = false;
    };
  }, []);

  const isWithinSubmissionWindow = useMemo(() => {
    if (!submissionPeriodStart || !submissionPeriodEnd) return false;
    return now >= submissionPeriodStart && now <= submissionPeriodEnd;
  }, [now, submissionPeriodStart, submissionPeriodEnd]);

  const submissionPeriodText = useMemo(() => {
    if (!submissionPeriodStart || !submissionPeriodEnd) return '';
    const format = (d: Date) =>
      d.toLocaleString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    return `Đã đến thời gian nộp giáo án. Thời gian nộp: ${format(submissionPeriodStart)} - Hết hạn: ${format(submissionPeriodEnd)}.`;
  }, [submissionPeriodStart, submissionPeriodEnd]);

  // Reset page when search, filter, or folder selection changes
  // Note: This is handled in the useEffect that loads lesson plans, so we don't need to duplicate it here

  // Load folders from API
  const loadFolders = async () => {
    if (!user || !user.id) {
      return; // Don't load if user is not logged in
    }
    try {
      setFoldersLoading(true);
      const foldersData = await lessonPlanFolderService.getFolders();
      setFolders(foldersData);
    } catch (err) {
      logger.error('Error loading folders:', err);
      // Don't show error to user, just log it - folders are optional
      // Set empty array to prevent UI errors
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  // Load folders on mount (only if user is logged in)
  useEffect(() => {
    if (user && user.id) {
      loadFolders();
    }
  }, [user]);

  // Load filter options from API
  const loadFilters = async () => {
    if (!user || !user.id) return;
    try {
      setFiltersLoading(true);
      const filters = await lessonPlanService.getLessonPlanFilters();
      setFilterTeachers(filters.teachers);
      setFilterSubjects(filters.subjects);
      setFilterYears(filters.years);
    } catch (err) {
      logger.error('Error loading filters:', err);
      setFilterTeachers([]);
      setFilterSubjects([]);
      setFilterYears([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.id) {
      loadFilters();
    }
  }, [user]);

  // Load subjects from database for "Theo bộ môn" filter in "GIÁO ÁN CỦA TÔI" tab
  const loadDbSubjects = async () => {
    if (!user || !user.id) return;
    try {
      setLoadingDbSubjects(true);
      logger.log('📚 Loading subjects from database for "Theo bộ môn" filter...');
      const result = await subjectService.getSubjects({
        page: 1,
        pageSize: 1000
      });
      const subjectsList = result.subjects || [];
      logger.log('✅ Loaded subjects from database:', subjectsList.length, 'subjects');
      setDbSubjects(subjectsList.map(s => ({ id: s.id, name: s.name })));
    } catch (err) {
      logger.error('❌ Error loading subjects from database:', err);
      setDbSubjects([]);
    } finally {
      setLoadingDbSubjects(false);
    }
  };

  useEffect(() => {
    if (user && user.id) {
      loadDbSubjects();
    }
  }, [user]);

  // Static list of 40 weeks for "Theo tuần" filter (tab GIÁO ÁN CỦA TÔI)
  useEffect(() => {
    // Always show 40 weeks (1-40) instead of only weeks with existing data
    setFilterWeeks(Array.from({ length: 40 }, (_, i) => i + 1));
  }, []);

  // Static list of 40 weeks for "Theo tuần" filter (tab QL GIÁO ÁN)
  useEffect(() => {
    // Always show 40 weeks (1-40) instead of only weeks with existing data
    setAllFilterWeeks(Array.from({ length: 40 }, (_, i) => i + 1));
  }, []);

  // Extract filter values from combinedFilters and selectedFolderKey (for folders)
  // Extract filter values from combinedFilters and selectedFolderKey (for folders)
  const getFilterFromFolderKey = () => {
    // Start with combined filters
    const result = {
      teacherId: combinedFilters.teacherId,
      year: combinedFilters.year,
      subjectName: null as string | null,
      subjectId: combinedFilters.subjectId,
      week: combinedFilters.week,
      folderId: null as string | null,
      dateFrom: null as Date | null,
      dateTo: null as Date | null
    };

    // Add folder filter if selected
    if (selectedFolderKey && selectedFolderKey !== 'all') {
      if (selectedFolderKey.startsWith('folder:')) {
        result.folderId = selectedFolderKey.substring('folder:'.length);
      }
      else if (selectedFolderKey.startsWith('subject:')) {
        const val = selectedFolderKey.substring('subject:'.length);
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
        if (isUUID) {
          result.subjectId = val;
        } else {
          result.subjectName = val;
        }
      }
      else if (selectedFolderKey.startsWith('week:')) {
        const val = parseInt(selectedFolderKey.substring('week:'.length));
        if (!isNaN(val)) result.week = val;
      }
      else if (selectedFolderKey.startsWith('year:')) {
        const val = parseInt(selectedFolderKey.substring('year:'.length));
        if (!isNaN(val)) {
          result.year = val;
          result.dateFrom = new Date(val, 0, 1);
          result.dateTo = new Date(val, 11, 31);
        }
      }
      // Teacher filter from folder tree
      else if (selectedFolderKey.startsWith('teacher:')) {
        result.teacherId = selectedFolderKey.substring('teacher:'.length);
      }
      // Semester: kept as client-side filtering or mapped to weeks if needed (skipped for API now)
    }

    return result;
  };

  // Load lesson plans when search term, filter, folder selection, combined filters, date range, or sortBy changes
  useEffect(() => {
    // Reset to page 1 when filters change
    setPage(1);
    setMyPage(1);
    loadLessonPlans(1, false);
    loadLessonPlans(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, statusFilter, selectedFolderKey, dateFrom, dateTo, combinedFilters, sortBy]);

  // Search and filter handlers
  const handleSearch = () => {
    loadLessonPlans(1, false);
    loadLessonPlans(1, true);
  };

  const handleClearFilters = () => {
    // Reset all filters to default (same as handleRefresh)
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    setCombinedFilters({ year: null, subjectId: null, week: null, teacherId: null, status: null });
    setSelectedFolderKey('all');
    setDateFrom(null);
    setDateTo(null);
    setPage(1);
    setMyPage(1);
  };

  const handleRefresh = () => {
    // Clear all filters first
    // Reset all filters to default
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    setCombinedFilters({ year: null, subjectId: null, week: null, teacherId: null, status: null });
    setSelectedFolderKey('all');
    setDateFrom(null);
    setDateTo(null);
    setPage(1);
    setMyPage(1);
  };

  const handleReloadCurrentPage = useCallback(() => {
    // Reload data for the current active tab and current page number
    if (activeTab === 0) {
      loadLessonPlans(myPage, true);
    } else {
      loadLessonPlans(page, false);
    }
  }, [activeTab, myPage, page, loadLessonPlans]);

  // Tab handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Reset folder filter khi chuyển tab
    setSelectedFolderKey('all');
    // Reset selected lesson plans khi chuyển tab
    setSelectedLessonPlanIds([]);
    // Reset to page 1 when switching tabs
    setPage(1);
    setMyPage(1);
  };

  // Page handlers
  const handlePageChange = (newPage: number) => {
    // Sau khi xóa tab "Tất cả giáo án":
    // - Tab 0: "Giáo án của tôi" (myPage, isMyPlans = true)
    // - Tab 1: "QL giáo án" (page, isMyPlans = false) - chỉ cho canApprove
    if (isTeacherOnly) {
      // Teacher only has "Giáo Án Của Tôi" tab (index 0)
      setMyPage(newPage);
      loadLessonPlans(newPage, true);
    } else {
      // Others: tab 0 = my plans, tab 1 = QL giáo án (admin)
      if (activeTab === 0) {
        setMyPage(newPage);
        loadLessonPlans(newPage, true);
      } else {
        setPage(newPage);
        loadLessonPlans(newPage, false);
      }
    }
  };

  // Lesson plan handlers
  const handleViewLessonPlan = async (id: string) => {
    try {
      // Always fetch fresh data from API instead of using cached state
      // This ensures we have the latest data, especially after revise/update operations
      const lessonPlan = await lessonPlanService.getLessonPlanById(id);
      setSelectedLessonPlan(lessonPlan);
      setDetailDialogOpen(true);
    } catch (err) {
      logger.error('Error loading lesson plan:', err);
      // Fallback to cached data if API call fails
      const lessonPlan = [...lessonPlans, ...myLessonPlans].find(lp => lp.id === id);
      if (lessonPlan) {
        setSelectedLessonPlan(lessonPlan);
        setDetailDialogOpen(true);
      }
    }
  };

  const handleDownloadLessonPlan = async (id: string) => {
    try {
      // Fetch full lesson plan details
      const lessonPlan = await lessonPlanService.getLessonPlanById(id);

      if (lessonPlan.is_digital) {
        // For digital plans, generate PDF
        const content = lessonPlan.content || '<p>Không có nội dung</p>';

        // Import html2pdf dynamically
        const html2pdf = (await import('html2pdf.js')).default;

        // Create a temporary container for PDF generation
        const element = document.createElement('div');
        element.innerHTML = `
          <div style="font-family: Arial, sans-serif; padding: 20px;">
            <h1 style="text-align: center; color: #333; margin-bottom: 20px;">${lessonPlan.title}</h1>
            <div style="margin-bottom: 30px;">
              <p><strong>Giáo viên:</strong> ${lessonPlan.submittedByUser?.fullName || 'N/A'}</p>
              <p><strong>Môn học:</strong> ${lessonPlan.subject || 'N/A'}</p>
              <p><strong>Tuần:</strong> ${lessonPlan.week || 'N/A'}</p>
            </div>
            <hr style="border: 1px solid #eee; margin-bottom: 30px;" />
            <div class="content">
              ${content}
            </div>
          </div>
        `;

        const opt = {
          margin: 10,
          filename: `${lessonPlan.title}.pdf`,
          image: { type: 'jpeg' as const, quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
        };

        // Generate and save
        html2pdf().set(opt).from(element).save();
        return;
      }

      // For file-based plans, use the blob download
      const blob = await lessonPlanService.downloadLessonPlan(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = lessonPlan.fileName || 'lesson-plan';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error downloading lesson plan:', err);
      setError('Không thể tải xuống giáo án. Vui lòng thử lại sau.');
    }
  };

  const handleDeleteLessonPlan = async (id: string) => {
    try {
      await lessonPlanService.deleteLessonPlan(id);
      showSuccess('Xóa giáo án thành công');
      handleReloadCurrentPage();
    } catch (err) {
      logger.error('Error deleting lesson plan:', err);
      showError('Không thể xóa giáo án. Vui lòng thử lại sau.');
      setError('Không thể xóa giáo án. Vui lòng thử lại sau.');
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkMoveClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    setBulkMoveDialogOpen(true);
    setSelectedMoveFolderId(null);
  };

  const handleBulkApproveClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    // Filter to only allow approving lesson plans with status Pending or UnderReview
    const approvableIds = lessonPlans
      .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
      .map(lp => lp.id);

    if (approvableIds.length === 0) {
      showError('Không có giáo án nào có thể duyệt. Chỉ có thể duyệt giáo án ở trạng thái "Chờ duyệt" hoặc "Đang xem xét".');
      return;
    }

    setBulkApproveDialogOpen(true);
    setBulkApprovalNotes('');
  };

  const handleBulkApproveConfirm = async () => {
    if (selectedLessonPlanIds.length === 0) return;

    try {
      setIsBulkProcessing(true);
      const approvableIds = lessonPlans
        .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
        .map(lp => lp.id);

      if (approvableIds.length === 0) {
        showError('Không có giáo án nào có thể duyệt.');
        return;
      }

      const response = await lessonPlanService.bulkApproveLessonPlans(approvableIds, bulkApprovalNotes.trim() || undefined);

      if (response.success) {
        // Use flushSync to ensure dialog closes immediately
        flushSync(() => {
          setBulkApproveDialogOpen(false);
          setBulkApprovalNotes('');
          setSelectedLessonPlanIds([]);
        });
        showSuccess(response.message || `Đã duyệt ${response.approvedCount} giáo án thành công`);
        // Reload after a short delay
        setTimeout(() => {
          handleReloadCurrentPage();
        }, 500);
      } else {
        showError(response.message || 'Không thể duyệt giáo án');
      }
    } catch (err) {
      logger.error('Error bulk approving lesson plans:', err);
      showError(err instanceof Error ? err.message : 'Không thể duyệt giáo án');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkRejectClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    // Filter to only allow rejecting lesson plans with status Pending or UnderReview
    const rejectableIds = lessonPlans
      .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
      .map(lp => lp.id);

    if (rejectableIds.length === 0) {
      showError('Không có giáo án nào có thể yêu cầu chỉnh sửa. Chỉ có thể yêu cầu chỉnh sửa giáo án ở trạng thái "Chờ duyệt" hoặc "Đang xem xét".');
      return;
    }

    setBulkRejectDialogOpen(true);
    setBulkRevisionNotes('');
  };

  const handleBulkRejectConfirm = async () => {
    if (selectedLessonPlanIds.length === 0 || !bulkRevisionNotes.trim()) {
      showError('Vui lòng nhập góp ý chỉnh sửa');
      return;
    }

    try {
      setIsBulkProcessing(true);
      const rejectableIds = lessonPlans
        .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
        .map(lp => lp.id);

      if (rejectableIds.length === 0) {
        showError('Không có giáo án nào có thể yêu cầu chỉnh sửa.');
        return;
      }

      const response = await lessonPlanService.bulkRejectLessonPlans(rejectableIds, bulkRevisionNotes.trim());

      if (response.success) {
        // Use flushSync to ensure dialog closes immediately
        flushSync(() => {
          setBulkRejectDialogOpen(false);
          setBulkRevisionNotes('');
          setSelectedLessonPlanIds([]);
        });
        showSuccess(response.message || `Đã yêu cầu chỉnh sửa ${response.rejectedCount} giáo án thành công`);
        // Reload after a short delay
        setTimeout(() => {
          handleReloadCurrentPage();
        }, 500);
      } else {
        showError(response.message || 'Không thể yêu cầu chỉnh sửa giáo án');
      }
    } catch (err) {
      logger.error('Error bulk rejecting lesson plans:', err);
      showError(err instanceof Error ? err.message : 'Không thể yêu cầu chỉnh sửa giáo án');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkMoveConfirm = async () => {
    if (selectedLessonPlanIds.length === 0) return;

    try {
      setIsMoving(true);
      const response = await lessonPlanFolderService.bulkMoveLessonPlansToFolder(
        selectedLessonPlanIds,
        selectedMoveFolderId
      );

      console.log('📦 Bulk Move Response:', response);
      // More robust success check including various possible response formats
      const isSuccess = response.success === true ||
        response.message === 'Moved successfully' ||
        (typeof response.movedCount === 'number' && response.movedCount > 0);
      if (isSuccess) {
        const count = response.movedCount ?? response.count ?? selectedLessonPlanIds.length;
        showSuccess(`Đã di chuyển ${count} giáo án vào folder thành công!`);
        setBulkMoveDialogOpen(false);
        setSelectedMoveFolderId(null);
        setSelectedLessonPlanIds([]);
        handleReloadCurrentPage();
      } else {
        showError(response.message || 'Có lỗi xảy ra khi di chuyển giáo án');
      }
    } catch (err: any) {
      logger.error('Error moving lesson plans to folder:', err);
      const errorMessage = err?.response?.data?.error || 'Không thể di chuyển giáo án. Vui lòng thử lại sau.';
      showError(errorMessage);
    } finally {
      setIsMoving(false);
    }
  };

  const handleBulkMoveCancel = () => {
    setBulkMoveDialogOpen(false);
    setSelectedMoveFolderId(null);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const response = await lessonPlanService.bulkDeleteLessonPlans(selectedLessonPlanIds);
      if (response.success) {
        showSuccess(`Đã xóa ${response.deletedCount} giáo án thành công`);
        // Use flushSync to force synchronous state update and close dialog immediately
        flushSync(() => {
          setBulkDeleteDialogOpen(false);
          setSelectedLessonPlanIds([]);
        });
        // Delay reload to ensure DB updates
        setTimeout(() => {
          handleReloadCurrentPage();
        }, 500);
      } else {
        showError(response.message || 'Có lỗi xảy ra khi xóa giáo án');
      }
    } catch (err) {
      logger.error('Error bulk deleting lesson plans:', err);
      showError('Không thể xóa giáo án. Vui lòng thử lại sau.');
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleReviseLessonPlan = async (id: string) => {
    try {
      // For now, just refresh the list. In a real app, this would call an API to request revision
      handleReloadCurrentPage();
    } catch (err) {
      logger.error('Error revising lesson plan:', err);
      setError('Không thể yêu cầu chỉnh sửa giáo án. Vui lòng thử lại sau.');
    }
  };

  const handleEditLessonPlan = async (lessonPlan: LessonPlan) => {
    try {
      // Fetch full lesson plan details (including content for digital plans)
      const fullLessonPlan = await lessonPlanService.getLessonPlanById(lessonPlan.id);
      setLessonPlanToEdit(fullLessonPlan);
      setEditDialogOpen(true);
    } catch (err) {
      logger.error('Error loading lesson plan for edit:', err);
      // Fallback to list data
      setLessonPlanToEdit(lessonPlan);
      setEditDialogOpen(true);
    }
  };

  const handleEditSuccess = async () => {
    setEditDialogOpen(false);
    setLessonPlanToEdit(null);
    showSuccess('Cập nhật giáo án thành công!');
    handleReloadCurrentPage();
  };

  const handleEditError = (error: string) => {
    showError(error);
  };

  const handleUploadSuccess = async () => {
    setUploadDialogOpen(false);
    showSuccess('Nộp giáo án thành công!');

    // Reset to page 1 for both tabs
    setPage(1);
    setMyPage(1);

    // Wait a bit to ensure DB transaction is committed
    await new Promise(resolve => setTimeout(resolve, 300));

    // Reload both tabs to ensure the new lesson plan appears
    // For teachers, focus on "Giáo Án Của Tôi" tab
    try {
      // First reload immediately - prioritize myPlans for teachers
      if (isTeacherOnly) {
        logger.log('📋 Teacher - Reloading my lesson plans after upload');
        await loadLessonPlans(1, true); // Reload my lesson plans first
      } else {
        await Promise.all([
          loadLessonPlans(1, false), // Reload all lesson plans
          loadLessonPlans(1, true)   // Reload my lesson plans
        ]);
      }

      // Also reload again after a short delay to ensure DB is fully updated
      setTimeout(async () => {
        if (isTeacherOnly) {
          logger.log('📋 Teacher - Second reload of my lesson plans after upload');
          await loadLessonPlans(1, true); // Reload my lesson plans
        } else {
          await Promise.all([
            loadLessonPlans(1, false), // Reload all lesson plans
            loadLessonPlans(1, true)   // Reload my lesson plans
          ]);
        }
      }, 800);
    } catch (err) {
      logger.error('Error reloading lesson plans after upload:', err);
      showError('❌ Có lỗi xảy ra khi tải lại danh sách giáo án');
    }
  };

  const handleUploadError = (error: string) => {
    // Close upload dialog immediately
    setUploadDialogOpen(false);
    setQuickUploadFolderId(null);

    // If error is about submission period, show error dialog (no snackbar)
    if (error.includes('Chưa đến hoặc đã hết thời gian nộp giáo án')) {
      // Parse error to extract period info
      let errorMessage = 'Chưa đến hoặc đã hết thời gian nộp giáo án. Vui lòng liên hệ người phụ trách.';
      let periodInfo = '';

      try {
        // Try to extract period from error message
        const jsonMatch = error.match(/\{.*\}/);
        if (jsonMatch) {
          const errorData = JSON.parse(jsonMatch[0]);
          if (errorData.period) {
            const startDate = new Date(errorData.period.startDate);
            const endDate = new Date(errorData.period.endDate);

            // Format: "16:15 30/11/2025 - 16:55 30/11/2025"
            const startStr = startDate.toLocaleString('vi-VN', {
              hour: '2-digit',
              minute: '2-digit',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
            const endStr = endDate.toLocaleString('vi-VN', {
              hour: '2-digit',
              minute: '2-digit',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });

            periodInfo = `Thời gian nộp giáo án (${startStr} - ${endStr})`;
          }
        }
      } catch (parseErr) {
        logger.warn('Error parsing error message:', parseErr);
      }

      setUploadError(periodInfo ? `${errorMessage}\n${periodInfo}` : errorMessage);
      setErrorDialogOpen(true);
    } else {
      // For other errors, show snackbar
      showError(error);
    }
  };

  // Helper functions
  const getCurrentLessonPlans = () => {
    // Sau khi xóa tab "Tất cả giáo án":
    // - Tab 0: "Giáo án của tôi" (myLessonPlans)
    // - Tab 1: "QL giáo án" (lessonPlans) - chỉ cho canApprove
    if (isTeacherOnly) {
      // Teacher only has "Giáo Án Của Tôi" tab (index 0)
      return activeTab === 0 ? myLessonPlans : [];
    } else {
      // Others: tab 0 = my plans, tab 1 = QL giáo án (admin)
      return activeTab === 0 ? myLessonPlans : lessonPlans;
    }
  };

  const getCurrentPage = () => {
    // Sau khi xóa tab "Tất cả giáo án":
    // - Tab 0: "Giáo án của tôi" (myPage)
    // - Tab 1: "QL giáo án" (page) - chỉ cho canApprove
    if (isTeacherOnly) {
      // Teacher only has "Giáo Án Của Tôi" tab (index 0)
      return activeTab === 0 ? myPage : page;
    } else {
      // Others: tab 0 = my plans, tab 1 = QL giáo án (admin)
      return activeTab === 0 ? myPage : page;
    }
  };

  const getTotalCount = () => {
    // Sau khi xóa tab "Tất cả giáo án":
    // - Tab 0: "Giáo án của tôi" (myTotalCount)
    // - Tab 1: "QL giáo án" (totalCount) - chỉ cho canApprove
    if (isTeacherOnly) {
      return activeTab === 0 ? myTotalCount : totalCount;
    } else {
      return activeTab === 0 ? myTotalCount : totalCount;
    }
  };

  const getTotalPages = () => {
    // Sau khi xóa tab "Tất cả giáo án":
    // - Tab 0: "Giáo án của tôi" (myTotalPages)
    // - Tab 1: "QL giáo án" (totalPages) - chỉ cho canApprove
    if (isTeacherOnly) {
      // Teacher only has "Giáo Án Của Tôi" tab (index 0)
      return activeTab === 0 ? myTotalPages : totalPages;
    } else {
      // Others: tab 0 = my plans, tab 1 = QL giáo án (admin)
      return activeTab === 0 ? myTotalPages : totalPages;
    }
  };

  // Virtual folders - nhóm theo năm / học kỳ / bộ môn / giáo viên
  const teacherSubjectFolders = useMemo(() => {
    if (!isTeacherRole || !Array.isArray(myLessonPlans)) return [];
    const subjects = new Set<string>();
    myLessonPlans.forEach(lp => {
      if (lp.subject && lp.subject.trim()) {
        subjects.add(lp.subject.trim());
      }
    });
    return Array.from(subjects).sort();
  }, [isTeacherRole, myLessonPlans]);

  const teacherYearFolders = useMemo(() => {
    if (!isTeacherRole || !Array.isArray(myLessonPlans)) return [];
    const years = new Set<number>();
    myLessonPlans.forEach(lp => {
      if (lp.submissionDate) {
        const year = new Date(lp.submissionDate).getFullYear();
        if (!Number.isNaN(year)) years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a); // năm mới trước
  }, [isTeacherRole, myLessonPlans]);

  const teacherSemesterFolders = useMemo(() => {
    if (!isTeacherRole || !Array.isArray(myLessonPlans)) return [];
    const semesters = new Set<number>();
    myLessonPlans.forEach(lp => {
      const week = lp.week ?? null;
      if (week && week > 0) {
        // Quy ước đơn giản: tuần 1–18 = HK1, >=19 = HK2
        const semester = week <= 18 ? 1 : 2;
        semesters.add(semester);
      }
    });
    return Array.from(semesters).sort();
  }, [isTeacherRole, myLessonPlans]);

  const approverTeacherFolders = useMemo(() => {
    if (isTeacherOnly || !canApprove || !Array.isArray(lessonPlans)) return [];
    const map = new Map<string, string>(); // id -> name
    lessonPlans.forEach(lp => {
      const teacherId = lp.submittedByUserId;
      const teacherName = lp.submittedByUser?.fullName || 'Không rõ giáo viên';
      if (!map.has(teacherId)) {
        map.set(teacherId, teacherName);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'vi'));
  }, [isTeacherOnly, canApprove, lessonPlans]);

  const allYearFolders = useMemo(() => {
    if (!Array.isArray(lessonPlans) || lessonPlans.length === 0) return [];
    const years = new Set<number>();
    lessonPlans.forEach(lp => {
      if (lp.submissionDate) {
        const year = new Date(lp.submissionDate).getFullYear();
        if (!Number.isNaN(year)) years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [lessonPlans]);

  const allSemesterFolders = useMemo(() => {
    if (!Array.isArray(lessonPlans) || lessonPlans.length === 0) return [];
    const semesters = new Set<number>();
    lessonPlans.forEach(lp => {
      const week = lp.week ?? null;
      if (week && week > 0) {
        const semester = week <= 18 ? 1 : 2;
        semesters.add(semester);
      }
    });
    return Array.from(semesters).sort();
  }, [lessonPlans]);




  // Build folder tree from API folders
  interface FolderWithChildren extends LessonPlanFolder {
    children: FolderWithChildren[];
  }

  const folderTree = useMemo(() => {
    const folderMap = new Map<string, FolderWithChildren>();
    const rootFolders: FolderWithChildren[] = [];

    // Initialize all folders with children array
    folders.forEach(folder => {
      folderMap.set(folder.id, { ...folder, children: [] });
    });

    // Build tree structure
    folders.forEach(folder => {
      const folderWithChildren = folderMap.get(folder.id)!;
      if (folder.parentFolderId && folderMap.has(folder.parentFolderId)) {
        const parent = folderMap.get(folder.parentFolderId)!;
        parent.children.push(folderWithChildren);
      } else {
        rootFolders.push(folderWithChildren);
      }
    });

    // Sort folders by name
    const sortFolders = (foldersList: FolderWithChildren[]) => {
      foldersList.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
      foldersList.forEach(f => {
        if (f.children.length > 0) {
          sortFolders(f.children);
        }
      });
    };

    sortFolders(rootFolders);
    return rootFolders;
  }, [folders]);

  const allSubjectFolders = useMemo(() => {
    if (!Array.isArray(lessonPlans) || lessonPlans.length === 0) return [];
    const subjects = new Set<string>();
    lessonPlans.forEach(lp => {
      if (lp.subject && lp.subject.trim()) {
        subjects.add(lp.subject.trim());
      }
    });
    return Array.from(subjects).sort();
  }, [lessonPlans]);

  const applyFolderFilter = (plans: LessonPlan[], folderKey: string): LessonPlan[] => {
    // Defensive check
    if (!plans || !Array.isArray(plans)) return [];

    // Note: Draft status is now filtered at backend level for QL GIÁO ÁN tab
    // No need to filter here anymore
    let filteredPlans = plans;

    if (!folderKey || folderKey === 'all') {
      return filteredPlans;
    }

    // Học kỳ: semester:<1|2> (dựa trên week)
    if (folderKey.startsWith('semester:')) {
      const semStr = folderKey.substring('semester:'.length);
      const sem = Number(semStr);
      if (!Number.isNaN(sem)) {
        return filteredPlans.filter(lp => {
          const week = lp.week ?? null;
          if (!week || week <= 0) return false;
          const semester = week <= 18 ? 1 : 2;
          return semester === sem;
        });
      }
    }

    return filteredPlans;
  };

  // Handlers for folder operations
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      showError('Vui lòng nhập tên folder');
      return;
    }

    try {
      await lessonPlanFolderService.createFolder({
        name: newFolderName.trim(),
        parentFolderId: newFolderParentId
      });
      showSuccess('Tạo folder thành công!');
      setCreateFolderDialogOpen(false);
      setNewFolderName('');
      setNewFolderParentId(null);
      await loadFolders();
    } catch (err) {
      logger.error('Error creating folder:', err);
      showError('Không thể tạo folder. Vui lòng thử lại sau.');
    }
  };

  const handleMoveToFolder = async (lessonPlanId: string, folderId: string | null) => {
    try {
      console.log(`📂 Moving lesson plan ${lessonPlanId} to folder ${folderId}`);
      const response = await lessonPlanFolderService.moveLessonPlanToFolder(lessonPlanId, folderId);
      console.log('📦 Single Move Response:', response);

      showSuccess('Di chuyển giáo án vào folder thành công!');
      // Xóa khỏi danh sách được chọn nếu có
      setSelectedLessonPlanIds(prev => prev.filter(id => id !== lessonPlanId));
      handleReloadCurrentPage(); // Reload lesson plans to show updated folder
    } catch (err) {
      logger.error('Error moving lesson plan to folder:', err);
      showError('Không thể di chuyển giáo án. Vui lòng thử lại sau.');
    }
  };

  // Handler for quick upload to folder
  const handleQuickUpload = (folderId: string) => {
    setQuickUploadFolderId(folderId);
    setUploadDialogOpen(true);
  };

  // Flatten folder tree for select dropdown (with indentation to show hierarchy)
  const flatFolders = useMemo(() => {
    const flatten = (folders: FolderWithChildren[], level: number = 0): Array<{ id: string; name: string; level: number }> => {
      const result: Array<{ id: string; name: string; level: number }> = [];
      folders.forEach(folder => {
        result.push({ id: folder.id, name: folder.name, level });
        if (folder.children.length > 0) {
          result.push(...flatten(folder.children, level + 1));
        }
      });
      return result;
    };
    return flatten(folderTree);
  }, [folderTree]);

  // Helper to count all nested children recursively
  const countAllNestedChildren = (folder: FolderWithChildren): number => {
    let count = folder.children.length;
    folder.children.forEach(child => {
      count += countAllNestedChildren(child);
    });
    return count;
  };

  // Helper to count all lesson plans in folder and nested folders recursively
  const countLessonPlansInFolder = (folder: FolderWithChildren): number => {
    if (!Array.isArray(lessonPlans)) return 0;
    let count = lessonPlans.filter(lp => lp.folderId === folder.id).length;
    if (folder.children && Array.isArray(folder.children)) {
      folder.children.forEach(child => {
        count += countLessonPlansInFolder(child);
      });
    }
    return count;
  };

  // Handler to open delete folder dialog
  const handleDeleteFolderClick = (folderId: string, folderName: string) => {
    // Check if folder has children or lesson plans
    const folder = folders.find(f => f.id === folderId);
    const folderInTree = findFolderInTree(folderTree, folderId);

    if (!folderInTree) {
      showError('Không tìm thấy folder');
      return;
    }

    // Count all nested children recursively
    const totalChildrenCount = countAllNestedChildren(folderInTree);

    // Count lesson plans in this folder and all nested folders
    const totalLessonPlansCount = countLessonPlansInFolder(folderInTree);

    // Build confirmation message
    let message = `Bạn có chắc chắn muốn xóa folder "${folderName}"?`;
    if (totalChildrenCount > 0 && totalLessonPlansCount > 0) {
      message += `\n\nFolder này chứa ${totalChildrenCount} folder con (bao gồm cả folder lồng nhau) và ${totalLessonPlansCount} giáo án. Tất cả sẽ bị xóa hoặc gỡ khỏi folder!`;
    } else if (totalChildrenCount > 0) {
      message += `\n\nFolder này chứa ${totalChildrenCount} folder con (bao gồm cả folder lồng nhau). Tất cả sẽ bị xóa!`;
    } else if (totalLessonPlansCount > 0) {
      message += `\n\nFolder này chứa ${totalLessonPlansCount} giáo án. Tất cả giáo án sẽ bị gỡ khỏi folder!`;
    }

    setFolderToDelete({ id: folderId, name: folderName });
    setDeleteFolderMessage(message);
    setDeleteFolderDialogOpen(true);
  };

  // Handler to confirm delete folder
  const handleDeleteFolderConfirm = async () => {
    if (!folderToDelete) return;

    try {
      await lessonPlanFolderService.deleteFolder(folderToDelete.id);
      showSuccess('Xóa folder thành công');
      loadFolders();
      // If deleted folder was selected, reset to "all"
      if (selectedFolderKey === `folder:${folderToDelete.id}`) {
        setSelectedFolderKey('all');
      }
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
      setDeleteFolderMessage('');
    } catch (err) {
      logger.error('Error deleting folder:', err);
      const error = err as Error;
      let errorMessage = 'Không thể xóa folder. Vui lòng thử lại sau.';

      // Parse error message from backend
      if (error.message) {
        if (error.message.includes('contains subfolders')) {
          errorMessage = 'Không thể xóa folder vì folder này chứa folder con. Vui lòng xóa tất cả folder con trước.';
        } else if (error.message.includes('contains lesson plans')) {
          errorMessage = 'Không thể xóa folder vì folder này chứa giáo án. Vui lòng di chuyển hoặc xóa tất cả giáo án trước.';
        } else {
          errorMessage = error.message;
        }
      }

      showError(errorMessage);
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
      setDeleteFolderMessage('');
    }
  };

  // Helper to find folder in tree
  const findFolderInTree = (tree: FolderWithChildren[], folderId: string): FolderWithChildren | null => {
    for (const folder of tree) {
      if (folder.id === folderId) return folder;
      if (folder.children.length > 0) {
        const found = findFolderInTree(folder.children, folderId);
        if (found) return found;
      }
    }
    return null;
  };

  const getDescendantFolderIds = (tree: FolderWithChildren[], rootFolderId: string): string[] => {
    const root = findFolderInTree(tree, rootFolderId);
    if (!root) return [];
    const ids: string[] = [];
    const walk = (node: FolderWithChildren) => {
      node.children.forEach(child => {
        ids.push(child.id);
        if (child.children.length > 0) walk(child);
      });
    };
    walk(root);
    return ids;
  };

  const handleEditFolderClick = (folder: LessonPlanFolder) => {
    setFolderToEdit({ id: folder.id, name: folder.name, parentFolderId: folder.parentFolderId || null });
    setEditFolderName(folder.name || '');
    setEditFolderParentId(folder.parentFolderId || null);
    setEditFolderDialogOpen(true);
  };

  const handleEditFolderCancel = () => {
    setEditFolderDialogOpen(false);
    setFolderToEdit(null);
    setEditFolderName('');
    setEditFolderParentId(null);
    setEditFolderSaving(false);
  };

  const handleEditFolderSave = async () => {
    if (!folderToEdit) return;
    const name = (editFolderName || '').trim();
    if (!name) {
      showError('Vui lòng nhập tên folder');
      return;
    }

    try {
      setEditFolderSaving(true);
      await lessonPlanFolderService.updateFolder(folderToEdit.id, {
        name,
        parentFolderId: editFolderParentId || null
      });
      showSuccess('Cập nhật folder thành công!');
      handleEditFolderCancel();
      await loadFolders();
    } catch (err) {
      logger.error('Error updating folder:', err);
      const error = err as Error;
      showError(error?.message || 'Không thể cập nhật folder. Vui lòng thử lại sau.');
    } finally {
      setEditFolderSaving(false);
    }
  };

  // Recursive component to render folder tree with multiple levels
  const FolderTreeItem: React.FC<{
    folder: FolderWithChildren;
    level?: number;
    onSelect: (folderId: string) => void;
    selectedFolderKey: string;
    onCreateSubfolder?: (parentId: string) => void;
    onDeleteFolder?: (folderId: string, folderName: string) => void;
    onQuickUpload?: (folderId: string) => void;
    canManage?: boolean;
    showOwnerName?: boolean;
  }> = ({ folder, level = 0, onSelect, selectedFolderKey, onCreateSubfolder, onDeleteFolder, onQuickUpload, canManage = true, showOwnerName = false }) => {
    const isSelected = selectedFolderKey === `folder:${folder.id}`;
    const paddingLeft = level * 2 + 1; // 1rem base + 2rem per level
    const [hovered, setHovered] = useState(false);
    const isOwner = !folder.isSystem && (folder.ownerUserId === user?.id || canApprove);

    return (
      <React.Fragment>
        <Box
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{
            position: 'relative',
            '&:hover .folder-actions': {
              display: 'flex'
            }
          }}
        >
          <ListItemButton
            selected={isSelected}
            onClick={() => onSelect(folder.id)}
            sx={{
              pl: `${paddingLeft}rem`,
              // Reserve space so actions don't overlap text when hovering
              pr: canManage && isOwner ? '7rem' : '1rem',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ListItemIcon>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              sx={{ minWidth: 0 }}
              primary={
                <Tooltip title={folder.name || ''} arrow>
                  <Typography variant="body2" noWrap>
                    {folder.name}
                  </Typography>
                </Tooltip>
              }
              secondary={
                showOwnerName && folder.ownerName ? (
                  <Tooltip title={folder.ownerName} arrow>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {folder.ownerName}
                    </Typography>
                  </Tooltip>
                ) : undefined
              }
            />
          </ListItemButton>
          {/* Show actions on hover for any folder (not only active/selected) */}
          {canManage && isOwner && (
            <Box
              className="folder-actions"
              sx={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                display: hovered ? 'flex' : 'none',
                flexDirection: 'row',
                direction: 'ltr',
                gap: 0.5,
                zIndex: 10,
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              onClick={(e) => e.stopPropagation()}
            >
              {onQuickUpload && (
                <Tooltip title="Nộp giáo án nhanh vào folder này">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuickUpload(folder.id);
                    }}
                    sx={{ p: 0.5, color: 'primary.main' }}
                    aria-label="Nộp giáo án nhanh"
                  >
                    <UploadFileIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onCreateSubfolder && (
                <Tooltip title="Tạo folder con">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateSubfolder(folder.id);
                    }}
                    sx={{ p: 0.5 }}
                    aria-label="Tạo folder con"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Chỉnh sửa folder">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditFolderClick(folder);
                  }}
                  sx={{ p: 0.5 }}
                  aria-label="Chỉnh sửa folder"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {onDeleteFolder && (
                <Tooltip title="Xóa folder">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteFolder(folder.id, folder.name);
                    }}
                    sx={{ p: 0.5, color: 'error.main' }}
                    aria-label="Xóa folder"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        {folder.children && folder.children.length > 0 && folder.children.map(child => (
          <FolderTreeItem
            key={child.id}
            folder={child}
            level={level + 1}
            onSelect={onSelect}
            selectedFolderKey={selectedFolderKey}
            onCreateSubfolder={onCreateSubfolder}
            onDeleteFolder={onDeleteFolder}
            onQuickUpload={onQuickUpload}
            canManage={canManage}
          />
        ))}
      </React.Fragment>
    );
  };

  // Handler for saving submission period
  const handleSaveSubmissionPeriod = async () => {
    if (!submissionPeriodStart || !submissionPeriodEnd) {
      showError('Vui lòng chọn đầy đủ thời gian bắt đầu và kết thúc');
      return;
    }

    if (submissionPeriodEnd <= submissionPeriodStart) {
      showError('Thời gian kết thúc phải sau thời gian bắt đầu');
      return;
    }

    try {
      setSubmissionPeriodLoading(true);
      await lessonPlanService.setSubmissionPeriod(submissionPeriodStart, submissionPeriodEnd);
      showSuccess('Cài đặt thời gian nộp giáo án thành công!');
      setSubmissionPeriodDialogOpen(false);
    } catch (error) {
      logger.error('Error setting submission period:', error);
      const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi cài đặt thời gian nộp';
      showError(errorMessage);
    } finally {
      setSubmissionPeriodLoading(false);
    }
  };

  // Export handlers
  const handleExportAll = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    const filter = getFilterFromFolderKey();
    return await lessonPlanService.exportLessonPlans({
      dateFrom,
      dateTo,
      submittedByUserId: filter.teacherId || undefined,
      folderId: filter.folderId || undefined,
      // @ts-ignore
      subjectId: filter.subjectId || undefined,
      subject: filter.subjectName || undefined,
      year: filter.year ? (typeof filter.year === 'number' ? filter.year : parseInt(filter.year)) : undefined,
      status: statusFilter || undefined
    });
  };

  const handleExportMy = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    return await lessonPlanService.exportLessonPlans({
      dateFrom,
      dateTo,
      submittedByUserId: user?.id,
      status: statusFilter || undefined
    });
  };

  const handleExportManage = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    return await lessonPlanService.exportLessonPlans({
      dateFrom,
      dateTo,
      status: statusFilter || undefined
    });
  };

  // Export column definitions
  const lessonPlanExportColumns: ExportColumn[] = [
    { key: 'title', label: 'Tiêu đề', width: 40 },
    { key: 'subject', label: 'Môn học', width: 20 },
    { key: 'week', label: 'Tuần', width: 12 },
    { key: 'submittedByUser.fullName', label: 'Người nộp', width: 25 },
    { key: 'submittedByUser.email', label: 'Email', width: 25 },
    { key: 'folderId', label: 'Thư mục', width: 30 },
    { key: 'submissionDate', label: 'Ngày nộp', width: 15 },
    { key: 'status', label: 'Trạng thái', width: 15 },
    { key: 'fileName', label: 'Tên file', width: 30 },
    { key: 'description', label: 'Mô tả', width: 40 }
  ];



  const handleDetailAction = useCallback((type: 'approve' | 'review' | 'reject') => {
    setDetailActionType(type);
    setDetailActionNotes('');
    setDetailActionDialogOpen(true);
  }, []);

  const handleDetailActionConfirm = async () => {
    if (!selectedLessonPlan) return;
    setDetailActionProcessing(true);
    try {
      if (detailActionType === 'approve') {
        await lessonPlanService.approveLessonPlan(selectedLessonPlan.id, detailActionNotes.trim() || undefined);
        showSuccess('Duyệt giáo án thành công!');
      } else if (detailActionType === 'review') {
        await lessonPlanService.reviewLessonPlan(selectedLessonPlan.id, detailActionNotes.trim() || undefined);
        showSuccess('Giáo án đã được chuyển sang trạng thái Đang xem xét!');
      } else if (detailActionType === 'reject') {
        if (!detailActionNotes.trim()) {
          showError('Vui lòng nhập lý do yêu cầu chỉnh sửa');
          setDetailActionProcessing(false);
          return;
        }
        await lessonPlanService.rejectLessonPlan(selectedLessonPlan.id, detailActionNotes.trim());
        showSuccess('Yêu cầu chỉnh sửa đã được gửi thành công!');
      }
      setDetailActionDialogOpen(false);
      setDetailDialogOpen(false); // Close detail dialog too
      handleReloadCurrentPage();
    } catch (err) {
      logger.error(`Error ${detailActionType} lesson plan:`, err);
      showError('Có lỗi xảy ra. Vui lòng thử lại.');
    } finally {
      setDetailActionProcessing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Quản Lý Giáo Án
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canApprove && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => {
                // Load current submission period
                lessonPlanService.getSubmissionPeriod().then(period => {
                  if (period && period.startDate && period.endDate) {
                    setSubmissionPeriodStart(new Date(period.startDate));
                    setSubmissionPeriodEnd(new Date(period.endDate));
                  } else {
                    setSubmissionPeriodStart(null);
                    setSubmissionPeriodEnd(null);
                  }
                  setSubmissionPeriodDialogOpen(true);
                }).catch(err => {
                  logger.error('Error loading submission period:', err);
                  setSubmissionPeriodStart(null);
                  setSubmissionPeriodEnd(null);
                  setSubmissionPeriodDialogOpen(true);
                });
              }}
            >
              Cài đặt thời gian nộp
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<CloneIcon />}
            onClick={() => setTemplateDialogOpen(true)}
            size="large"
          >
            Tạo từ mẫu
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setInitialUploadFiles([]);
              setUploadDialogOpen(true);
            }}
            size="large"
          >
            Nộp giáo án mới
          </Button>
        </Box>
      </Box>

      {/* Submission period banner (marquee) */}
      {isWithinSubmissionWindow && submissionPeriodText && (
        <Box
          sx={{
            mb: 2,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: 'warning.light'
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              '@keyframes lessonPlanMarquee': {
                '0%': { transform: 'translateX(100%)' },
                '100%': { transform: 'translateX(-100%)' }
              }
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
                animation: 'lessonPlanMarquee 18s linear infinite'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.contrastText' }}>
                {submissionPeriodText}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Search and Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '2fr 0.9fr 1fr 1fr auto'
                },
                gap: 2,
                alignItems: 'center'
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Tìm kiếm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Trạng thái</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Trạng thái"
                >
                  {LESSON_PLAN_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="Từ ngày"
                value={dateFrom}
                onChange={(newValue) => {
                  setDateFrom(newValue);
                  setPage(1);
                  setMyPage(1);
                }}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DatePicker
                label="Đến ngày"
                value={dateTo}
                onChange={(newValue) => {
                  setDateTo(newValue);
                  setPage(1);
                  setMyPage(1);
                }}
                minDate={dateFrom || undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Làm mới
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs + layout 2 cột: cây thư mục ảo + list */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={`Giáo án của tôi${myTotalCount > 0 ? ` (${myTotalCount})` : ''}`} />
            {canApprove && <Tab label={`QL giáo án${totalCount > 0 ? ` (${totalCount})` : ''}`} />}
          </Tabs>
        </Box>

        {/* Tab: Giáo án của tôi */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '345px 1fr' }, gap: 2 }}>
            {/* Cột trái: cây thư mục ảo theo năm / học kỳ / bộ môn / tuần */}
            <Box
              sx={{
                borderRight: { md: 1, xs: 0 },
                borderColor: 'divider',
                pr: { md: 2, xs: 0 },
                mb: { xs: 2, md: 0 }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Thư mục giáo án
                </Typography>
                <Tooltip title="Tạo folder mới">
                  <IconButton
                    size="small"
                    onClick={() => setCreateFolderDialogOpen(true)}
                    sx={{ ml: 1 }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Clear all filters button - Tab GIÁO ÁN CỦA TÔI */}
              {(combinedFilters.year || combinedFilters.subjectId || combinedFilters.week || combinedFilters.teacherId || searchTerm || dateFrom || dateTo || statusFilter) && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={handleRefresh}
                  sx={{ mb: 1, width: '100%' }}
                >
                  Xóa tất cả bộ lọc
                </Button>
              )}

              <List
                dense
                component="nav"
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
              >
                <ListItemButton
                  selected={selectedFolderKey === 'all'}
                  onClick={() => setSelectedFolderKey('all')}
                >
                  <ListItemIcon>
                    <FolderOpenIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Tất cả giáo án" />
                </ListItemButton>
              </List>

              {Array.isArray(filterYears) && filterYears.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ px: 1, pb: 1 }}>
                    <Autocomplete
                      options={filterYears}
                      value={combinedFilters.year}
                      onChange={(_, newValue) => {
                        setCombinedFilters(prev => ({ ...prev, year: newValue }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Theo năm học"
                          placeholder="Tìm kiếm năm..."
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <CalendarMonthIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} fontSize="small" />
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <CalendarMonthIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                          Năm {option}
                        </Box>
                      )}
                      getOptionLabel={(option) => `Năm ${option}`}
                      noOptionsText="Không tìm thấy năm học"
                      sx={{ width: '100%' }}
                    />
                  </Box>
                </>
              )}

              {Array.isArray(dbSubjects) && dbSubjects.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ px: 1, pb: 1 }}>
                    <Autocomplete
                      options={dbSubjects}
                      value={dbSubjects.find(s => s.id === combinedFilters.subjectId) || null}
                      onChange={(_, newValue) => {
                        setCombinedFilters(prev => ({ ...prev, subjectId: newValue?.id || null }));
                      }}
                      getOptionLabel={(option) => option.name}
                      loading={loadingDbSubjects}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Theo bộ môn"
                          placeholder="Tìm kiếm bộ môn..."
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <SchoolIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} fontSize="small" />
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <SchoolIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                          {option.name}
                        </Box>
                      )}
                      noOptionsText="Không tìm thấy bộ môn"
                      sx={{ width: '100%' }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  </Box>
                </>
              )}

              {Array.isArray(filterWeeks) && filterWeeks.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ px: 1, pb: 1 }}>
                    <Autocomplete
                      options={filterWeeks}
                      value={combinedFilters.week}
                      onChange={(_, newValue) => {
                        setCombinedFilters(prev => ({ ...prev, week: newValue }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Theo tuần"
                          placeholder="Tìm kiếm tuần..."
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <EventNoteIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} fontSize="small" />
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <EventNoteIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                          Tuần {option}
                        </Box>
                      )}
                      getOptionLabel={(option) => `Tuần ${option}`}
                      noOptionsText="Không tìm thấy tuần"
                      sx={{ width: '100%' }}
                    />
                  </Box>
                </>
              )}

              {/* Folder thật từ API - Recursive tree với nhiều cấp */}
              {Array.isArray(folderTree) && folderTree.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <List
                    dense
                    component="nav"
                    sx={{ bgcolor: 'background.paper', borderRadius: 1, maxHeight: 480, overflowY: 'auto' }}
                  >
                    {folderTree.map(folder => (
                      <FolderTreeItem
                        key={folder.id}
                        folder={folder}
                        level={0}
                        onSelect={(folderId) => setSelectedFolderKey(`folder:${folderId}`)}
                        selectedFolderKey={selectedFolderKey}
                        onCreateSubfolder={(parentId) => {
                          setNewFolderParentId(parentId);
                          setCreateFolderDialogOpen(true);
                        }}
                        onDeleteFolder={handleDeleteFolderClick}
                        onQuickUpload={handleQuickUpload}
                        canManage={true}
                        showOwnerName={false}
                      />
                    ))}
                  </List>
                </>
              )}
            </Box>

            {/* Cột phải: list giáo án */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <StatusLegendRow />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Sắp xếp theo</InputLabel>
                    <Select
                      value={sortBy}
                      label="Sắp xếp theo"
                      onChange={(e) => setSortBy(e.target.value as 'title' | 'submissiondate')}
                    >
                      <MenuItem value="title">Tên giáo án (ABC)</MenuItem>
                      <MenuItem value="submissiondate">Thời gian upload</MenuItem>
                    </Select>
                  </FormControl>
                  {selectedLessonPlanIds.length > 0 && (
                    <>
                      <Tooltip title={`Di chuyển (${selectedLessonPlanIds.length})`} arrow>
                        <IconButton
                          color="primary"
                          onClick={handleBulkMoveClick}
                          aria-label="Di chuyển"
                          size="small"
                        >
                          <DriveFileMoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Xóa (${selectedLessonPlanIds.length})`} arrow>
                        <IconButton
                          color="error"
                          onClick={handleBulkDeleteClick}
                          aria-label="Xóa"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Xuất Excel" arrow>
                    <IconButton
                      color="primary"
                      onClick={() => setExportAllDialogOpen(true)}
                      aria-label="Xuất Excel"
                      size="small"
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <LessonPlanErrorBoundary onRetry={handleReloadCurrentPage}>
                <LessonPlanList
                  lessonPlans={applyFolderFilter(getCurrentLessonPlans(), selectedFolderKey)}
                  loading={loading}
                  error={error}
                  page={getCurrentPage()}
                  totalPages={getTotalPages()}
                  totalCount={getTotalCount()}
                  onPageChange={handlePageChange}
                  onRefresh={handleReloadCurrentPage}
                  onViewLessonPlan={handleViewLessonPlan}
                  onDownloadLessonPlan={handleDownloadLessonPlan}
                  onDeleteLessonPlan={handleDeleteLessonPlan}
                  onReviseLessonPlan={handleReviseLessonPlan}
                  onEditLessonPlan={handleEditLessonPlan}
                  showMyPlans={true}
                  showAdminActions={false}
                  selectedLessonPlanIds={selectedLessonPlanIds}
                  onSelectedIdsChange={setSelectedLessonPlanIds}
                  onBulkDeleteClick={handleBulkDeleteClick}
                  onBulkMoveClick={handleBulkMoveClick}
                  folders={folders}
                  showStatusLegend={false}
                />
              </LessonPlanErrorBoundary>
            </Box>
          </Box>
        </TabPanel>

        {/* Tab: QL giáo án cho BGH/Approver */}
        {canApprove && (
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '345px 1fr' }, gap: 2 }}>
              {/* Cột trái: cây thư mục theo giáo viên */}
              <Box
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: 'divider',
                  pr: { md: 2, xs: 0 },
                  mb: { xs: 2, md: 0 }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Thư mục quản lý
                  </Typography>
                  <Tooltip title="Tạo folder mới">
                    <IconButton
                      size="small"
                      onClick={() => setCreateFolderDialogOpen(true)}
                      sx={{ ml: 1 }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Clear all filters button - Tab QL GIÁO ÁN */}
                {(combinedFilters.year || combinedFilters.subjectId || combinedFilters.week || combinedFilters.teacherId || searchTerm || dateFrom || dateTo || statusFilter) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={handleRefresh}
                    sx={{ mb: 1, width: '100%' }}
                  >
                    Xóa tất cả bộ lọc
                  </Button>
                )}

                <List
                  dense
                  component="nav"
                  sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                >
                  <ListItemButton
                    selected={selectedFolderKey === 'all'}
                    onClick={() => setSelectedFolderKey('all')}
                  >
                    <ListItemIcon>
                      <FolderOpenIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Tất cả giáo án" />
                  </ListItemButton>
                </List>

                {Array.isArray(filterYears) && filterYears.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ px: 1, pb: 1 }}>
                      <Autocomplete
                        options={filterYears}
                        value={combinedFilters.year}
                        onChange={(_, newValue) => {
                          setCombinedFilters(prev => ({ ...prev, year: newValue }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Theo năm học"
                            placeholder="Tìm kiếm năm..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <CalendarMonthIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} fontSize="small" />
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <CalendarMonthIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                            Năm {option}
                          </Box>
                        )}
                        getOptionLabel={(option) => `Năm ${option}`}
                        noOptionsText="Không tìm thấy năm học"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  </>
                )}

                {Array.isArray(dbSubjects) && dbSubjects.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ px: 1, pb: 1 }}>
                      <Autocomplete
                        options={dbSubjects}
                        value={dbSubjects.find(s => s.id === combinedFilters.subjectId) || null}
                        onChange={(_, newValue) => {
                          setCombinedFilters(prev => ({ ...prev, subjectId: newValue?.id || null }));
                        }}
                        getOptionLabel={(option) => option.name}
                        loading={loadingDbSubjects}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Theo bộ môn"
                            placeholder="Tìm kiếm bộ môn..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <SchoolIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} fontSize="small" />
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <SchoolIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                            {option.name}
                          </Box>
                        )}
                        noOptionsText="Không tìm thấy bộ môn"
                        sx={{ width: '100%' }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Box>
                  </>
                )}

                {/* Theo tuần - hiển thị list tuần có trong dữ liệu */}
                {Array.isArray(allFilterWeeks) && allFilterWeeks.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ px: 1, pb: 1 }}>
                      <Autocomplete
                        options={allFilterWeeks}
                        value={combinedFilters.week}
                        onChange={(_, newValue) => {
                          setCombinedFilters(prev => ({ ...prev, week: newValue }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Theo tuần"
                            placeholder="Tìm kiếm tuần..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <EventNoteIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} fontSize="small" />
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <EventNoteIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                            Tuần {option}
                          </Box>
                        )}
                        getOptionLabel={(option) => `Tuần ${option}`}
                        noOptionsText="Không tìm thấy tuần"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  </>
                )}

                {Array.isArray(filterTeachers) && filterTeachers.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ px: 1, pb: 1 }}>
                      <Autocomplete
                        options={filterTeachers}
                        value={filterTeachers.find(t => t.id === combinedFilters.teacherId) || null}
                        onChange={(_, newValue) => {
                          setCombinedFilters(prev => ({ ...prev, teacherId: newValue?.id || null }));
                        }}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Theo giáo viên"
                            placeholder="Tìm kiếm giáo viên..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <PersonIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} fontSize="small" />
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                            {option.name}
                          </Box>
                        )}
                        noOptionsText="Không tìm thấy giáo viên"
                        sx={{ width: '100%' }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Box>
                  </>
                )}

                {/* Folder thật từ API - Recursive tree với nhiều cấp */}
                {Array.isArray(folderTree) && folderTree.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <List
                      dense
                      component="nav"
                      sx={{ bgcolor: 'background.paper', borderRadius: 1, maxHeight: 480, overflowY: 'auto' }}
                    >
                      {folderTree.map(folder => (
                        <FolderTreeItem
                          key={folder.id}
                          folder={folder}
                          level={0}
                          onSelect={(folderId) => setSelectedFolderKey(`folder:${folderId}`)}
                          selectedFolderKey={selectedFolderKey}
                          onCreateSubfolder={(parentId) => {
                            setNewFolderParentId(parentId);
                            setCreateFolderDialogOpen(true);
                          }}
                          onDeleteFolder={handleDeleteFolderClick}
                          onQuickUpload={handleQuickUpload}
                          canManage={true}
                          showOwnerName={false}
                        />
                      ))}
                    </List>
                  </>
                )}
              </Box>

              {/* Cột phải: list giáo án */}
              <Box>
                {canExport && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <StatusLegendRow />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Sắp xếp theo</InputLabel>
                        <Select
                          value={sortBy}
                          label="Sắp xếp theo"
                          onChange={(e) => setSortBy(e.target.value as 'title' | 'submissiondate')}
                        >
                          <MenuItem value="title">Tên giáo án (ABC)</MenuItem>
                          <MenuItem value="submissiondate">Thời gian upload</MenuItem>
                        </Select>
                      </FormControl>
                      {selectedLessonPlanIds.length > 0 && (
                        <>
                          <Tooltip title={`Duyệt (${selectedLessonPlanIds.length})`} arrow>
                            <IconButton
                              color="success"
                              onClick={handleBulkApproveClick}
                              aria-label="Duyệt"
                              size="small"
                              disabled={isBulkProcessing}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Yêu cầu chỉnh sửa (${selectedLessonPlanIds.length})`} arrow>
                            <IconButton
                              color="error"
                              onClick={handleBulkRejectClick}
                              aria-label="Yêu cầu chỉnh sửa"
                              size="small"
                              disabled={isBulkProcessing}
                            >
                              <ReturnIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Di chuyển (${selectedLessonPlanIds.length})`} arrow>
                            <IconButton
                              color="primary"
                              onClick={handleBulkMoveClick}
                              aria-label="Di chuyển"
                              size="small"
                              disabled={isBulkProcessing}
                            >
                              <DriveFileMoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Xóa (${selectedLessonPlanIds.length})`} arrow>
                            <IconButton
                              color="error"
                              onClick={handleBulkDeleteClick}
                              aria-label="Xóa"
                              size="small"
                              disabled={isBulkProcessing}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Xuất Excel" arrow>
                        <IconButton
                          color="primary"
                          onClick={() => setExportManageDialogOpen(true)}
                          aria-label="Xuất Excel"
                          size="small"
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}
                {!loading && !error && lessonPlans.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Hiện chưa có giáo án nào <b>đã nộp</b> để quản lý (tab này không hiển thị <b>Bản nháp</b>).
                    Vui lòng nộp/chuyển trạng thái giáo án sang <b>Chờ nộp</b> / <b>Chờ duyệt</b> / <b>Đang xem xét</b>… để xuất hiện tại đây.
                  </Alert>
                )}
                <LessonPlanErrorBoundary onRetry={handleReloadCurrentPage}>
                  <LessonPlanList
                    lessonPlans={applyFolderFilter(getCurrentLessonPlans(), selectedFolderKey)}
                    loading={loading}
                    error={error}
                    page={getCurrentPage()}
                    totalPages={getTotalPages()}
                    totalCount={getTotalCount()}
                    onPageChange={handlePageChange}
                    onRefresh={handleReloadCurrentPage}
                    onViewLessonPlan={handleViewLessonPlan}
                    onDownloadLessonPlan={handleDownloadLessonPlan}
                    onDeleteLessonPlan={handleDeleteLessonPlan}
                    onReviseLessonPlan={handleReviseLessonPlan}
                    showMyPlans={false}
                    showAdminActions={true}
                    selectedLessonPlanIds={selectedLessonPlanIds}
                    onSelectedIdsChange={setSelectedLessonPlanIds}
                    onBulkDeleteClick={handleBulkDeleteClick}
                    onBulkMoveClick={handleBulkMoveClick}
                    folders={folders}
                    showStatusLegend={false}
                  />
                </LessonPlanErrorBoundary>
              </Box>
            </Box>
          </TabPanel>
        )}
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => {
        setUploadDialogOpen(false);
        setQuickUploadFolderId(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Nộp giáo án mới</DialogTitle>
        <DialogContent>
          <LessonPlanErrorBoundary onRetry={() => setUploadDialogOpen(false)}>
            <LessonPlanUpload
              onUploadSuccess={handleUploadSuccess}
              onUploadError={handleUploadError}
              onClose={() => {
                setUploadDialogOpen(false);
                setQuickUploadFolderId(null);
                setInitialUploadFiles([]);
              }}
              defaultFolderId={quickUploadFolderId}
              folders={folders}
              initialFiles={initialUploadFiles}
            />
          </LessonPlanErrorBoundary>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setQuickUploadFolderId(null);
          }}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      {lessonPlanToEdit && (
        <Dialog open={editDialogOpen} onClose={() => {
          setEditDialogOpen(false);
          setLessonPlanToEdit(null);
        }} maxWidth="md" fullWidth>
          <DialogTitle>Chỉnh sửa giáo án</DialogTitle>
          <DialogContent>
            <LessonPlanErrorBoundary onRetry={() => setEditDialogOpen(false)}>
              <LessonPlanEdit
                lessonPlan={lessonPlanToEdit}
                onEditSuccess={handleEditSuccess}
                onEditError={handleEditError}
                onClose={() => {
                  setEditDialogOpen(false);
                  setLessonPlanToEdit(null);
                }}
                folders={folders}
              />
            </LessonPlanErrorBoundary>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditDialogOpen(false);
              setLessonPlanToEdit(null);
            }}>
              Đóng
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Detail Dialog */}
      <LessonPlanErrorBoundary onRetry={() => setDetailDialogOpen(false)}>
        <LessonPlanDetail
          lessonPlan={selectedLessonPlan}
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          onMoveToFolder={handleMoveToFolder}
          folders={folders}
          canApprove={canApprove}
          onApprove={() => handleDetailAction('approve')}
          onReview={() => handleDetailAction('review')}
          onReject={() => handleDetailAction('reject')}
        />
      </LessonPlanErrorBoundary>

      {/* Detail Action Dialog (Approve, Review, Reject) */}
      <Dialog open={detailActionDialogOpen} onClose={() => setDetailActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {detailActionType === 'approve' ? 'Duyệt giáo án' :
            detailActionType === 'review' ? 'Chuyển sang Đang xem xét' :
              'Yêu cầu chỉnh sửa'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedLessonPlan?.title}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={detailActionType === 'reject' ? "Lý do (Bắt buộc)" : "Ghi chú (Tùy chọn)"}
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={detailActionNotes}
            onChange={(e) => setDetailActionNotes(e.target.value)}
            required={detailActionType === 'reject'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailActionDialogOpen(false)}>Hủy</Button>
          <Button
            onClick={handleDetailActionConfirm}
            variant="contained"
            color={detailActionType === 'reject' ? 'warning' : detailActionType === 'approve' ? 'success' : 'info'}
            disabled={detailActionProcessing}
          >
            {detailActionProcessing ? 'Đang xử lý...' : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        open={createFolderDialogOpen}
        onClose={() => {
          setCreateFolderDialogOpen(false);
          setNewFolderName('');
          setNewFolderParentId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tạo folder mới</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Tên folder *"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Ví dụ: Giáo án Toán 7 - Học kỳ 1"
              autoFocus
            />
            <FolderSelector
              folders={folders}
              value={newFolderParentId}
              onChange={setNewFolderParentId}
              label="Folder cha (tùy chọn)"
              allowNone={true}
              noneLabel="Không có (folder gốc)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateFolderDialogOpen(false);
              setNewFolderName('');
              setNewFolderParentId(null);
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            Tạo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog
        open={editFolderDialogOpen}
        onClose={handleEditFolderCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Chỉnh sửa folder</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Tên folder *"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              placeholder="Nhập tên folder..."
              autoFocus
              disabled={editFolderSaving}
            />
            <FolderSelector
              folders={(() => {
                if (!folderToEdit) return folders;
                const descendantIds = getDescendantFolderIds(folderTree, folderToEdit.id);
                const excluded = new Set<string>([folderToEdit.id, ...descendantIds]);
                return folders.filter(f => !excluded.has(f.id));
              })()}
              value={editFolderParentId}
              onChange={setEditFolderParentId}
              label="Folder cha (tùy chọn)"
              allowNone={true}
              noneLabel="Không có (folder gốc)"
            />
            <Alert severity="info">
              Bạn có thể đổi tên hoặc chuyển folder sang một folder cha khác.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditFolderCancel} disabled={editFolderSaving}>
            Hủy
          </Button>
          <Button onClick={handleEditFolderSave} variant="contained" disabled={editFolderSaving || !editFolderName.trim()}>
            {editFolderSaving ? <CircularProgress size={20} /> : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <Dialog
        open={deleteFolderDialogOpen}
        onClose={() => {
          setDeleteFolderDialogOpen(false);
          setFolderToDelete(null);
          setDeleteFolderMessage('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Xác nhận xóa folder</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {deleteFolderMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteFolderDialogOpen(false);
              setFolderToDelete(null);
              setDeleteFolderMessage('');
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleDeleteFolderConfirm}
            variant="contained"
            color="error"
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Period Settings Dialog (Admin/BGH only) */}
      {canApprove && (
        <Dialog
          open={submissionPeriodDialogOpen}
          onClose={() => {
            setSubmissionPeriodDialogOpen(false);
            setSubmissionPeriodStart(null);
            setSubmissionPeriodEnd(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Cài đặt thời gian nộp giáo án</DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <DateTimePicker
                  label="Thời gian bắt đầu"
                  value={submissionPeriodStart}
                  onChange={(newValue) => setSubmissionPeriodStart(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DateTimePicker
                  label="Thời gian kết thúc"
                  value={submissionPeriodEnd}
                  onChange={(newValue) => setSubmissionPeriodEnd(newValue)}
                  minDate={submissionPeriodStart || undefined}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Alert severity="info">
                  Giáo viên chỉ có thể nộp giáo án trong khoảng thời gian này. Nếu nộp ngoài thời gian, hệ thống sẽ từ chối.
                </Alert>
              </Box>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSubmissionPeriodDialogOpen(false);
                setSubmissionPeriodStart(null);
                setSubmissionPeriodEnd(null);
              }}
              disabled={submissionPeriodLoading}
            >
              Hủy
            </Button>
            <Button
              onClick={handleSaveSubmissionPeriod}
              variant="contained"
              disabled={submissionPeriodLoading || !submissionPeriodStart || !submissionPeriodEnd}
            >
              {submissionPeriodLoading ? <CircularProgress size={20} /> : 'Lưu'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk Move Dialog */}
      <Dialog open={bulkMoveDialogOpen} onClose={handleBulkMoveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Di chuyển {selectedLessonPlanIds.length} giáo án vào folder</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FolderSelector
              folders={folders}
              value={selectedMoveFolderId}
              onChange={setSelectedMoveFolderId}
              label="Chọn folder đích"
              allowNone={true}
              noneLabel="Không có folder (gỡ khỏi folder)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkMoveCancel} disabled={isMoving}>
            Hủy
          </Button>
          <Button
            onClick={handleBulkMoveConfirm}
            variant="contained"
            disabled={isMoving}
            startIcon={isMoving ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {isMoving ? 'Đang di chuyển...' : 'Di chuyển'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approve Dialog */}
      <Dialog open={bulkApproveDialogOpen} onClose={() => { setBulkApproveDialogOpen(false); setBulkApprovalNotes(''); }} maxWidth="md" fullWidth>
        <DialogTitle>Duyệt nhiều giáo án</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bạn đang duyệt {lessonPlans.filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview')).length} giáo án
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Ghi chú duyệt (tùy chọn)"
            value={bulkApprovalNotes}
            onChange={(e) => setBulkApprovalNotes(e.target.value)}
            placeholder="Nhập ghi chú duyệt cho tất cả giáo án..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkApproveDialogOpen(false); setBulkApprovalNotes(''); }} disabled={isBulkProcessing}>
            Hủy
          </Button>
          <Button
            onClick={handleBulkApproveConfirm}
            variant="contained"
            color="success"
            disabled={isBulkProcessing}
            startIcon={isBulkProcessing ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {isBulkProcessing ? 'Đang duyệt...' : 'Duyệt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Reject Dialog */}
      <Dialog open={bulkRejectDialogOpen} onClose={() => { setBulkRejectDialogOpen(false); setBulkRevisionNotes(''); }} maxWidth="md" fullWidth>
        <DialogTitle>Yêu cầu chỉnh sửa nhiều giáo án</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn đang yêu cầu chỉnh sửa {lessonPlans.filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview')).length} giáo án
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Góp ý chỉnh sửa *"
            value={bulkRevisionNotes}
            onChange={(e) => setBulkRevisionNotes(e.target.value)}
            placeholder="Nhập góp ý chỉnh sửa cho tất cả giáo án..."
            required
            error={bulkRejectDialogOpen && !bulkRevisionNotes.trim()}
            helperText={bulkRejectDialogOpen && !bulkRevisionNotes.trim() ? 'Góp ý chỉnh sửa là bắt buộc' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkRejectDialogOpen(false); setBulkRevisionNotes(''); }} disabled={isBulkProcessing}>
            Hủy
          </Button>
          <Button
            onClick={handleBulkRejectConfirm}
            variant="contained"
            color="error"
            disabled={isBulkProcessing || !bulkRevisionNotes.trim()}
            startIcon={isBulkProcessing ? <CircularProgress size={20} /> : <ReturnIcon />}
          >
            {isBulkProcessing ? 'Đang xử lý...' : 'Yêu cầu chỉnh sửa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel}>
        <DialogTitle>Xác nhận xóa nhiều giáo án</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xóa {selectedLessonPlanIds.length} giáo án không?
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Hành động này không thể hoàn tác. Tất cả dữ liệu và file liên quan đến các giáo án này sẽ bị xóa vĩnh viễn.
          </Typography>
          {!canApprove && (
            <Typography variant="body2" color="text.secondary">
              Lưu ý: Bạn chỉ có thể xóa giáo án do chính bạn nộp.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel}>Hủy</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained" disabled={selectedLessonPlanIds.length === 0}>
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialogs */}
      {!isTeacherRole && (
        <ExportDialog
          open={exportAllDialogOpen}
          onClose={() => setExportAllDialogOpen(false)}
          onExport={handleExportAll}
          columns={lessonPlanExportColumns}
          fileName="Danh_sach_tat_ca_giao_an"
          sheetName="Tất cả giáo án"
          title="Xuất danh sách tất cả giáo án ra Excel"
          description="Chọn khoảng thời gian để xuất dữ liệu giáo án. Dữ liệu sẽ được xuất theo ngày nộp."
          requireDateRange={true}
        />
      )}

      <ExportDialog
        open={exportMyDialogOpen}
        onClose={() => setExportMyDialogOpen(false)}
        onExport={handleExportMy}
        columns={lessonPlanExportColumns}
        fileName="Danh_sach_giao_an_cua_toi"
        sheetName="Giáo án của tôi"
        title="Xuất danh sách giáo án của tôi ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu giáo án của tôi. Dữ liệu sẽ được xuất theo ngày nộp."
        requireDateRange={true}
      />

      {canApprove && (
        <ExportDialog
          open={exportManageDialogOpen}
          onClose={() => setExportManageDialogOpen(false)}
          onExport={handleExportManage}
          columns={lessonPlanExportColumns}
          fileName="Danh_sach_quan_ly_giao_an"
          sheetName="Quản lý giáo án"
          title="Xuất danh sách quản lý giáo án ra Excel"
          description="Chọn khoảng thời gian để xuất dữ liệu quản lý giáo án. Dữ liệu sẽ được xuất theo ngày nộp."
          requireDateRange={true}
        />
      )}

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />

      <TemplateSelectionDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSelect={handleTemplateSelect}
      />

      {/* Error Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          setUploadError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Không thể nộp giáo án
        </DialogTitle>
        <DialogContent>
          {uploadError && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {uploadError.split('\n')[0]}
              </Typography>
              {uploadError.includes('\n') && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {uploadError.split('\n')[1]}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setErrorDialogOpen(false);
              setUploadError(null);
            }}
            variant="contained"
            color="error"
          >
            Đã hiểu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LessonPlanManagementPage;
